// ============================================================================
// Search Results Component
// Feature: 035-app-shell-enhancement
// Created: 2026-01-21
// Description: Component for displaying search results in the command palette
//              with capacity type badges, icons, and navigation support.
// User Story: US1 (Global Search)
// ============================================================================

import React from 'react';
import { Command } from 'cmdk';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

import { SearchResult } from '../../types/shell';

interface SearchResultsProps {
  /** Array of search results to display */
  results: SearchResult[];
  /** Current search query for highlighting */
  query: string;
  /** Callback when a result is selected */
  onSelect: (result: SearchResult) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Search results component for the command palette
 * 
 * Features:
 * - Displays search results with icons and descriptions
 * - Shows capacity type badges (F32, F64, etc.)
 * - Highlights matching text in results
 * - Navigates to result path on selection
 * - Keyboard navigation support via cmdk
 */
export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  query,
  onSelect,
  className,
}) => {
  const navigate = useNavigate();

  // Highlight matching text in result title/description
  const highlightText = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) return text;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 dark:bg-yellow-900/40 text-[var(--color-text-primary)] dark:text-yellow-100">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Get capacity type badge styling
  const getCapacityBadgeClass = (capacityType: string): string => {
    const baseClass = 'px-2 py-1 text-xs font-medium rounded';
    
    // Color coding based on capacity type
    switch (capacityType?.charAt(0)?.toUpperCase()) {
      case 'F': // Fabric capacities (F32, F64, etc.)
        return `${baseClass} bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300`;
      case 'P': // Premium capacities (P1, P2, etc.)
        return `${baseClass} bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300`;
      case 'A': // Azure capacities
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300`;
      default:
        return `${baseClass} bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]`;
    }
  };

  // Get type-specific icon styling
  const getTypeIconClass = (type: string): string => {
    switch (type) {
      case 'workspace':
        return 'text-blue-500';
      case 'policy':
        return 'text-orange-500';
      case 'backup':
        return 'text-green-500';
      case 'restore':
        return 'text-yellow-500';
      case 'alert':
        return 'text-red-500';
      default:
        return 'text-[var(--color-text-secondary)]';
    }
  };

  // Handle result selection
  const handleSelect = (result: SearchResult) => {
    onSelect(result);
    
    // Navigate to the result path
    if (result.path) {
      navigate(result.path);
    }
  };

  if (!results?.length) {
    return null;
  }

  return (
    <div className={clsx('divide-y divide-[var(--color-border)]', className)}>
      {results.map((result) => (
        <Command.Item
          key={result.id}
          value={result.id}
          onSelect={() => handleSelect(result)}
          className={clsx(
            'flex items-start gap-3 px-4 py-3 cursor-pointer',
            'hover:bg-[var(--color-bg-subtle)] transition-colors duration-150',
            'data-[selected=true]:bg-blue-50 dark:data-[selected=true]:bg-blue-900/20 data-[selected=true]:border-l-2 data-[selected=true]:border-blue-500'
          )}
        >
          {/* Icon */}
          <div className={clsx('flex-shrink-0 text-xl', getTypeIconClass(result.type))}>
            {result.icon}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              {/* Title */}
              <h3 className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                {highlightText(result.title ?? result.name, query)}
              </h3>
              
              {/* Capacity Badge */}
              {result.capacityType && (
                <span className={getCapacityBadgeClass(result.capacityType)}>
                  {result.capacityType}
                </span>
              )}
            </div>

            {/* Description */}
            {result.description && (
              <p className="text-sm text-[var(--color-text-secondary)] line-clamp-1">
                {highlightText(result.description, query)}
              </p>
            )}

            {/* Path */}
            {result.path && (
              <p className="text-xs text-[var(--color-text-secondary)] mt-1 font-mono">
                {result.path}
              </p>
            )}

            {/* Additional metadata */}
            <div className="flex items-center gap-4 mt-2 text-xs text-[var(--color-text-secondary)]">
              {/* Type */}
              <span className="capitalize">{result.type}</span>
              
              {/* Last updated (if available) */}
              {result.lastUpdated && (
                <>
                  <span>•</span>
                  <span>Updated {result.lastUpdated}</span>
                </>
              )}
              
              {/* Status (if available) */}
              {result.status && (
                <>
                  <span>•</span>
                  <span
                    className={clsx(
                      'px-1.5 py-0.5 rounded text-xs',
                      {
                        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300': result.status === 'active',
                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300': result.status === 'pending',
                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300': result.status === 'error',
                        'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]': result.status === 'inactive',
                      }
                    )}
                  >
                    {result.status}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Navigation arrow */}
          <div className="flex-shrink-0 text-[var(--color-text-secondary)]">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </Command.Item>
      ))}
    </div>
  );
};

export default SearchResults;